import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  NetworkId,
  NetworkIdPrivileges,
} from 'data/networkId/networkId.types';
import * as actions from 'data/networkId/networkId.actions';
import * as selectors from 'data/networkId/networkId.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as collectionFunctionGroupActions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import cloneDeep from 'lodash.clonedeep';
import NetworkIdListGrid from './components/NetworkIdListGrid';
import { distributorColumnDefs } from '../../NetworkId.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getNetworkIdList(state),
  selectedNetworkId: selectors.getSelectedNetworkId(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
});

const mapDispatch = {
  goToNetworkIdCreate: actions.goToNetworkIdCreate,
  goToNetworkIdView: actions.goToNetworkIdView,
  selectNetworkId: actions.selectNetworkId,
  getNetworkIdList: actions.getNetworkIdList,
  deleteNetworkId: actions.deleteNetworkId,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  getCollectionFunctionGroups: collectionFunctionGroupActions.getCollectionFunctionGroupList,
  fetchCollectionFunctionList: collectionFunctionActions.fetchCollectionFunctionList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const NetworkIdList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedNetworkId,
  goToNetworkIdCreate,
  goToNetworkIdView,
  selectNetworkId,
  getNetworkIdList,
  deleteNetworkId,
  getAccessFunctions,
  getCollectionFunctionGroups,
  fetchCollectionFunctionList,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [dataGrid, setDataGrid] = useState<NetworkId[] | []>([]);
  const [columnDefs] = useState<ColDef[]>(distributorColumnDefs);

  useEffect(() => {
    getNetworkIdList(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
    getCollectionFunctionGroups(deliveryFunctionId);
    fetchCollectionFunctionList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getNetworkIdList,
    getAccessFunctions,
    getCollectionFunctionGroups,
    fetchCollectionFunctionList,
  ]);

  useEffect(() => {
    if (data) {
      const clonedData = cloneDeep(data);
      setDataGrid(clonedData);
    }
  }, [data]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getNetworkIdList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToNetworkIdCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectNetworkId(event.api.getSelectedRows());
  };

  const handleSelectItemMenuAction = () => {
    goToNetworkIdView(history);
  };

  const handleDeleteRow = () => {
    const selectedNetwork = selectedNetworkId[0];
    if (selectedNetwork == null) {
      return;
    }

    const { networkId } = selectedNetwork;

    const distributorConfigrationId = selectedNetwork.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedNetwork.networkId as string,
        AuditService.NETWORK,
        AuditActionType.NETWORK_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteNetworkId(deliveryFunctionId, distributorConfigrationId, body, history, networkId);
  };

  const hasPrivilegeToView = privileges.includes(NetworkIdPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(NetworkIdPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(NetworkIdPrivileges.Delete);

  return (
    <Fragment>
      <NetworkIdListGrid
        data={dataGrid}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        lastUpdatedAt={lastUpdatedAt}
        onDeleteRow={handleDeleteRow}
        selectedNetworkId={selectedNetworkId[0]}
      />
    </Fragment>
  );
};

export default withRouter(connector(NetworkIdList));
